import React, { useEffect } from 'react';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { useLanguage } from '../../../../context/LanguageContext';
import Consult from '../../sections/Consult';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../../routes/paths';
import { Features } from '../../../../assets/features/Features';
import SEO from '../../seo/SEO';
import { seoData } from '../../../../data/seoData';

const TermosPage: React.FC = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Initialize AOS animation library
    AOS.init({
      duration: 1000,
      easing: 'ease-in-out',
      once: true,
      mirror: false
    });
  }, []);

  // Handle appointment button click
  const handleAppointmentClick = () => {
    navigate(ROUTES.HOME);
    setTimeout(() => {
      const appointmentSection = document.getElementById('appointment');
      if (appointmentSection) {
        appointmentSection.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  const termsContent = Features.termsPage[language];

  return (
    <>
      <SEO 
        title={seoData.termos.title}
        description={seoData.termos.description}
        canonical={seoData.termos.canonical}
      />
      {/* Hero Section */}
      <section 
        className="terms-hero d-flex align-items-center" 
        style={{ 
          backgroundColor: '#1977cc',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          height: '30vh',
          position: 'relative'
        }}
      >
        <div className="container">
          <div className="row">
            <div className="col-12 text-center text-white">
              <h1 
                className="display-4 fw-bold" 
                data-aos="fade-up" 
                style={{ 
                  color: '#ffffff', 
                  textShadow: '2px 2px 4px rgba(0, 0, 0, 0.5)'
                }}
              >
                {language === 'en' ? 'Terms and Conditions' : 'Termos e Condições'}
              </h1>
              <div className="divider mx-auto my-4" data-aos="fade-up" data-aos-delay="100" style={{
                width: '80px',
                height: '4px',
                background: '#ffffff',
                margin: '0 auto'
              }}></div>
              <p className="lead" data-aos="fade-up" data-aos-delay="200">
                {language === 'en' ? 'Important information about our services' : 'Informações importantes sobre os nossos serviços'}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Terms Content */}
      <section className="terms-content py-5">
        <div className="container">
          <div className="row">
            <div className="col-lg-10 mx-auto">
              <div className="bg-white p-4 p-md-5 rounded shadow-sm" data-aos="fade-up">
                <h2 className="mb-4">{language === 'en' ? 'Terms and Conditions' : 'Termos e Condições'}</h2>
                
                <div className="alert alert-info mb-4">
                  <p className="mb-0">
                    <strong>{language === 'en' ? 'Last Updated:' : 'Última Atualização:'}</strong> {language === 'en' ? 'August 6, 2026' : 'Porto, 6 de agosto de 2026'}
                  </p>
                </div>
                
                <div className="mb-4">
                  <p>
                    {termsContent.introduction}
                  </p>
                  <p className="fw-bold">
                    {termsContent.readCarefully}
                  </p>
                  <p className="fw-bold">
                    {termsContent.agreement}
                  </p>
                </div>
                
                <div className="mb-4">
                  <h4 className="mb-3">I. {termsContent.sections.definition.title}</h4>
                  <p>1. {termsContent.sections.definition.content}</p>
                </div>
                
                <div className="mb-4">
                  <h4 className="mb-3">II. {termsContent.sections.links.title}</h4>
                  <p>1. {termsContent.sections.links.content1}</p>
                  <p>2. {termsContent.sections.links.content2}</p>
                  <p>3. {termsContent.sections.links.content3}</p>
                  <p>4. {termsContent.sections.links.content4}</p>
                  <p>5. {termsContent.sections.links.content5}</p>
                  <p>6. {termsContent.sections.links.content6}</p>
                </div>
                
                <div className="mb-4">
                  <h4 className="mb-3">III. {termsContent.sections.usage.title}</h4>
                  <p>1. {termsContent.sections.usage.content1}</p>
                  <p>2. {termsContent.sections.usage.content2}</p>
                </div>
                
                <div className="mb-4">
                  <h4 className="mb-3">IV. {termsContent.sections.technical.title}</h4>
                  <p>1. {termsContent.sections.technical.content1}</p>
                  <p>2. {termsContent.sections.technical.content2}</p>
                  <p>3. {termsContent.sections.technical.content3}</p>
                  <p>4. {termsContent.sections.technical.content4}</p>
                  <p>5. {termsContent.sections.technical.content5}</p>
                  <p>6. {termsContent.sections.technical.content6}</p>
                  <p>7. {termsContent.sections.technical.content7}</p>
                </div>
                
                <div className="mb-4">
                  <h4 className="mb-3">V. {termsContent.sections.intellectual.title}</h4>
                  <p>1. {termsContent.sections.intellectual.content1}</p>
                  <p>2. {termsContent.sections.intellectual.content2}</p>
                </div>
                
                <div className="mb-4">
                  <h4 className="mb-3">VI. {termsContent.sections.ai.title}</h4>
                  <p>1. {termsContent.sections.ai.content}</p>
                </div>
                
                <div className="mb-4">
                  <h4 className="mb-3">VII. {termsContent.sections.communications.title}</h4>
                  <p>1. {termsContent.sections.communications.content1}</p>
                  <p>2. {termsContent.sections.communications.content2}</p>
                </div>
                
                <div className="mb-4">
                  <h4 className="mb-3">VIII. {termsContent.sections.transfer.title}</h4>
                  <p>1. {termsContent.sections.transfer.content1}</p>
                  <p>2. {termsContent.sections.transfer.content2}</p>
                </div>
                
                <div className="mb-4">
                  <h4 className="mb-3">IX. {termsContent.sections.privacy.title}</h4>
                  <p>1. {termsContent.sections.privacy.content1}</p>
                  <p>2. {termsContent.sections.privacy.content2}</p>
                  <p>3. {termsContent.sections.privacy.content3}</p>
                  <p>4. {termsContent.sections.privacy.content4}</p>
                </div>
                
                <div className="mb-4">
                  <h4 className="mb-3">X. {termsContent.sections.complaints.title}</h4>
                  <p>{termsContent.sections.complaints.content}</p>
                </div>
                
                <div className="mb-4">
                  <h4 className="mb-3">XI. {termsContent.sections.law.title}</h4>
                  <p>1. {termsContent.sections.law.content1}</p>
                  <p>2. {termsContent.sections.law.content2}</p>
                  <p>3. {termsContent.sections.law.content3}</p>
                </div>
                
                <div className="mt-5">
                  <p className="fw-bold">
                    {termsContent.finalDeclaration}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Consult Section */}
      <Consult onAppointmentClick={handleAppointmentClick} />
    </>
  );
};

export default TermosPage; 